import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '@data-access';
import { catchError, EMPTY, finalize } from 'rxjs';

import { CommonModule } from '@angular/common';
import { InputNavigationDirective } from '@core/directives';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

@Component({
  selector: 'fl-forgot-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    FloatLabelModule,
    MessageModule,
    InputNavigationDirective,
  ],
  template: `
    <form [formGroup]="form()" (ngSubmit)="onSubmit()" class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <p-float-label>
          <input pInputText id="email" type="email" formControlName="email" class="w-full" />
          <label for="email">Inserisci la tua email</label>
        </p-float-label>
        @if (form().controls['email'].touched && form().controls['email'].invalid) {
          <p-message severity="error" size="small" variant="simple">Inserisci un indirizzo email valido</p-message>
        }
      </div>
      <p-button
        type="submit"
        label="Invia Richiesta"
        styleClass="w-full"
        [loading]="loading()"
        [disabled]="!form().valid || loading()"
        severity="primary">
      </p-button>
      @if (emailSent()) {
        <div class="text-green-700 text-sm mt-2 text-center">
          Se l'email è presente nei nostri sistemi riceverai una mail con le istruzioni.
        </div>
      }
      <p-button
        [link]="true"
        label="Torna alla login"
        icon="pi pi-arrow-left"
        class="mt-4"
        (onClick)="onBackToLogin()"></p-button>
    </form>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotPasswordComponent {
  private readonly authService = inject(AuthService);
  protected readonly emailSent = signal(false);
  protected readonly loading = signal(false);
  protected readonly form = signal(
    inject(FormBuilder).group({
      email: ['', [Validators.required, Validators.email]],
    })
  );
  backToLogin = output<void>();

  protected onSubmit = () => {
    if (this.form().invalid) return;
    this.loading.set(true);
    this.authService
      .authResetPasswordPOST({ email: this.form().value.email || '' })
      .pipe(
        finalize(() => this.loading.set(false)),
        catchError(() => {
          this.emailSent.set(false);
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.emailSent.set(true);
      });
  };

  protected onBackToLogin = () => {
    this.backToLogin.emit();
  };
}
